import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Loader2, UserPlus, Power, PowerOff, KeyRound, Search, ChevronLeft, ChevronRight } from "lucide-react";
import { PageLoader } from "@/components/PageLoader";

export default function Users() {
  const [users, setUsers] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [munis, setMunis] = useState([]);
  const [form, setForm] = useState({
    username: "",
    email: "",
    contact_number: "",
    password: "",
    role: "user",
    district: "",
    municipality: "",
  });
  const [loading, setLoading] = useState(false);
  const [resetUser, setResetUser] = useState(null);
  const [newPassword, setNewPassword] = useState("");
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const [loadingUsers, setLoadingUsers] = useState(true);
  const pageSize = 10;

  const load = () =>
    api
      .get("/users")
      .then((r) => setUsers(r.data))
      .finally(() => setLoadingUsers(false));

  useEffect(() => {
    load();
    api.get("/districts").then((r) => setDistricts(r.data));
  }, []);

  const onDistrict = (val) => {
    setForm((f) => ({ ...f, district: val, municipality: "" }));
    api
      .get(`/districts/${encodeURIComponent(val)}/municipalities`)
      .then((r) => setMunis(r.data));
  };

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/users", form);
      toast.success(`User "${form.username}" created`);
      setForm({ username: "", email: "", contact_number: "", password: "", role: "user", district: "", municipality: "" });
      setMunis([]);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed to create user");
    } finally {
      setLoading(false);
    }
  };

  const toggleStatus = async (u) => {
    try {
      await api.patch(`/users/${u.id}/status`, { active: !u.active });
      toast.success(`User ${!u.active ? "enabled" : "disabled"}`);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed to update status");
    }
  };

  const doReset = async () => {
    if (newPassword.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    try {
      await api.post(`/users/${resetUser.id}/reset-password`, { password: newPassword });
      toast.success(`Password reset for "${resetUser.username}"`);
      setResetUser(null);
      setNewPassword("");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed to reset password");
    }
  };

  const filteredUsers = users.filter((u) => {
    const s = q.toLowerCase();
    return (
      String(u.id).includes(s) ||
      (u.username || "").toLowerCase().includes(s) ||
      (u.email || "").toLowerCase().includes(s) ||
      (u.contact_number || "").toLowerCase().includes(s) ||
      (u.role || "").toLowerCase().includes(s) ||
      (u.district || "").toLowerCase().includes(s) ||
      (u.municipality || "").toLowerCase().includes(s)
    );
  });

  const totalPages = Math.max(1, Math.ceil(filteredUsers.length / pageSize));
  const currentPage = Math.min(page, totalPages);
  const pagedUsers = filteredUsers.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  useEffect(() => {
    setPage(1);
  }, [q]);

  return (
    <div className="space-y-6" data-testid="users-page">
      <div>        <h1 className="font-heading text-2xl sm:text-3xl font-extrabold tracking-tight text-slate-900">
          User Management
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          Create system users and assign roles.
        </p>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <Card className="p-5 sm:p-6 border-slate-200 lg:col-span-1 h-fit">
          <h2 className="font-heading text-lg font-semibold text-slate-900 mb-4">
            Create User
          </h2>
          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label htmlFor="username">Username</Label>
              <Input
                id="username"
                data-testid="user-username-input"
                value={form.username}
                onChange={(e) => setForm((f) => ({ ...f, username: e.target.value }))}
                className="mt-1.5"
                required
              />
            </div>
            <div>
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                data-testid="user-email-input"
                value={form.email}
                onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                className="mt-1.5"
                required
              />
            </div>
            <div>
              <Label htmlFor="contact">Contact Number</Label>
              <Input
                id="contact"
                data-testid="user-contact-input"
                value={form.contact_number}
                onChange={(e) => setForm((f) => ({ ...f, contact_number: e.target.value }))}
                className="mt-1.5"
                required
              />
            </div>
            <div>
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                data-testid="user-password-input"
                value={form.password}
                onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))}
                className="mt-1.5"
                minLength={6}
                required
              />
            </div>
            <div>
              <Label>Role</Label>
              <Select
                value={form.role}
                onValueChange={(v) => setForm((f) => ({ ...f, role: v }))}
              >
                <SelectTrigger className="mt-1.5 bg-white" data-testid="admin-user-role-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-white">
                  <SelectItem value="user">User</SelectItem>
                  <SelectItem value="admin">Admin</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>District</Label>
              <Select value={form.district} onValueChange={onDistrict}>
                <SelectTrigger className="mt-1.5 bg-white" data-testid="user-district-select">
                  <SelectValue placeholder="Select district" />
                </SelectTrigger>
                <SelectContent className="bg-white max-h-72">
                  {districts.map((d) => (
                    <SelectItem key={d.id} value={d.name}>
                      {d.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Municipality</Label>
              <Select
                value={form.municipality}
                onValueChange={(v) => setForm((f) => ({ ...f, municipality: v }))}
                disabled={!form.district}
              >
                <SelectTrigger className="mt-1.5 bg-white" data-testid="user-municipality-select">
                  <SelectValue placeholder="Select municipality" />
                </SelectTrigger>
                <SelectContent className="bg-white max-h-72">
                  {munis.map((m) => (
                    <SelectItem key={m.id} value={m.name}>
                      {m.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button
              type="submit"
              data-testid="admin-create-user-button"
              className="w-full bg-blue-900 hover:bg-blue-800"
              disabled={loading}
            >
              {loading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <>
                  <UserPlus className="h-4 w-4 mr-2" /> Create User
                </>
              )}
            </Button>
          </form>
        </Card>

        <Card className="border-slate-200 lg:col-span-2 overflow-hidden">
          <div className="p-5 sm:p-6 pb-0 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <h2 className="font-heading text-lg font-semibold text-slate-900">
              System Users
            </h2>
            <div className="relative w-full sm:w-64">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search users…"
                className="pl-9 bg-white"
                data-testid="users-search-input"
              />
            </div>
          </div>
          <div className="p-5 sm:p-6 pt-4 overflow-x-auto">
            {loadingUsers ? (
              <PageLoader label="Loading users…" />
            ) : (
            <Table data-testid="users-table">
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead>ID</TableHead>
                  <TableHead>Username</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Contact</TableHead>
                  <TableHead>Role</TableHead>
                  <TableHead>District</TableHead>
                  <TableHead>Municipality</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredUsers.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9} className="text-center text-muted-foreground py-10">
                      No users found.
                    </TableCell>
                  </TableRow>
                ) : (
                  pagedUsers.map((u) => (
                  <TableRow key={u.id}>
                    <TableCell className="font-mono text-sm font-semibold text-blue-900">
                      {u.id}
                    </TableCell>
                    <TableCell className="font-medium">{u.username}</TableCell>
                    <TableCell>{u.email}</TableCell>
                    <TableCell>{u.contact_number || "—"}</TableCell>
                    <TableCell>
                      <Badge
                        className={
                          u.role === "admin"
                            ? "bg-blue-900 hover:bg-blue-900"
                            : "bg-slate-200 text-slate-700 hover:bg-slate-200"
                        }
                      >
                        {u.role}
                      </Badge>
                    </TableCell>
                    <TableCell>{u.district || "—"}</TableCell>
                    <TableCell>{u.municipality || "—"}</TableCell>
                    <TableCell>
                      <Badge
                        className={
                          u.active
                            ? "bg-emerald-100 text-emerald-700 hover:bg-emerald-100"
                            : "bg-rose-100 text-rose-700 hover:bg-rose-100"
                        }
                        data-testid={`user-status-${u.id}`}
                      >
                        {u.active ? "Active" : "Disabled"}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1.5">
                        <Button
                          variant="outline"
                          size="sm"
                          className="h-8 px-2"
                          onClick={() => toggleStatus(u)}
                          data-testid={`toggle-status-${u.id}`}
                          title={u.active ? "Disable account" : "Enable account"}
                        >
                          {u.active ? (
                            <PowerOff className="h-3.5 w-3.5" />
                          ) : (
                            <Power className="h-3.5 w-3.5" />
                          )}
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          className="h-8 px-2"
                          onClick={() => setResetUser(u)}
                          data-testid={`reset-password-${u.id}`}
                          title="Reset password"
                        >
                          <KeyRound className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                )))}
              </TableBody>
            </Table>
            )}
          </div>
          {!loadingUsers && filteredUsers.length > 0 && (
            <div
              className="flex items-center justify-between px-5 sm:px-6 py-4 border-t border-slate-200"
              data-testid="users-pagination"
            >
              <p className="text-sm text-muted-foreground">
                Showing {(currentPage - 1) * pageSize + 1}–
                {Math.min(currentPage * pageSize, filteredUsers.length)} of {filteredUsers.length}
              </p>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={currentPage <= 1}
                  data-testid="users-pagination-prev-btn"
                >
                  <ChevronLeft className="h-4 w-4 mr-1" /> Prev
                </Button>
                <span className="text-sm font-medium px-2" data-testid="users-pagination-page-info">
                  Page {currentPage} of {totalPages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={currentPage >= totalPages}
                  data-testid="users-pagination-next-btn"
                >
                  Next <ChevronRight className="h-4 w-4 ml-1" />
                </Button>
              </div>
            </div>
          )}
        </Card>
      </div>

      <Dialog
        open={!!resetUser}
        onOpenChange={(o) => {
          if (!o) {
            setResetUser(null);
            setNewPassword("");
          }
        }}
      >
        <DialogContent className="bg-white" data-testid="reset-password-modal">
          <DialogHeader>
            <DialogTitle>Reset Password</DialogTitle>
            <DialogDescription>
              Set a new password for {resetUser?.username}.
            </DialogDescription>
          </DialogHeader>
          <div>
            <Label htmlFor="new-password">New Password</Label>
            <Input
              id="new-password"
              type="password"
              data-testid="reset-password-input"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              placeholder="At least 6 characters"
              className="mt-1.5"
            />
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setResetUser(null);
                setNewPassword("");
              }}
            >
              Cancel
            </Button>
            <Button
              className="bg-blue-900 hover:bg-blue-800"
              onClick={doReset}
              data-testid="reset-password-confirm-btn"
            >
              Reset Password
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
